/**
 * Portable Text Renderer for Rich CMS Content
 * 
 * A comprehensive rich text renderer that transforms Sanity's Portable Text
 * format into beautifully styled React components with full multimedia support.
 * 
 * Supported Content Types:
 * - Rich text with custom formatting (bold, italic, links)
 * - Images with responsive sizing and captions
 * - Video embeds with multiple platform support
 * - Custom typography with brand fonts
 * - Block quotes and callouts
 * - Lists and structured content
 * 
 * Image Features:
 * - Multiple display styles (full-width, body-width, small)
 * - Responsive sizing with Next.js optimization
 * - Caption support from CMS
 * - Fallback handling for missing images
 * - Automatic aspect ratio preservation
 * 
 * Video Integration:
 * - YouTube and Vimeo embed support
 * - Responsive aspect ratios
 * - Custom VideoEmbed component integration
 * - Secure iframe embedding
 * 
 * Typography & Styling:
 * - Brand-consistent font application
 * - Responsive typography scaling
 * - Proper spacing and hierarchy
 * - Accessible color contrast
 * - Custom link styling with hover effects
 * 
 * Performance Optimizations:
 * - Lazy loading for images and videos
 * - Efficient component rendering
 * - Minimal DOM manipulation
 * - Optimized CSS delivery
 * 
 * @param {object} content - Portable Text content array from Sanity
 * @param {string} className - Additional CSS classes for styling
 * 
 * @description Rich content renderer with multimedia support for Sanity Portable Text
 */

import React from 'react';
import { PortableText as SanityPortableText } from '@portabletext/react';
import Image from 'next/image';
import { urlForImage } from '../../src/app/studio/lib/image';
import { sanityLoader } from '../../utils/sanityLoader';
import VideoEmbed from './VideoEmbed';

/**
 * Component configuration for Portable Text rendering
 * Maps Sanity content types to React components with WoodAlps styling
 */
const components = {
  types: {
    image: ({ value }) => {
      if (!value?.asset?._ref) {
        return null;
      }
      
      // Handle different display styles
      const getWidthClass = (displayStyle) => {
        switch (displayStyle) {
          case 'full-width':
            return 'w-full mx-auto';
          case 'small':
            return 'w-full md:w-1/2 mx-auto';
          case 'body-width':
          default:
            return 'w-full mx-auto';
        }
      };
      
      const widthClass = getWidthClass(value.displayStyle);
      
      const imageUrl = urlForImage(value);
      
      // Only render if we have a valid image URL
      if (!imageUrl) {
        console.warn('Invalid image in Portable Text:', value);
        return null;
      }
      
      return (
        <div className={`relative ${widthClass} my-8 rounded-lg overflow-hidden`}>
          <div className="relative w-full h-64 md:h-96">
            <Image
              src={imageUrl}
              alt={value.alt || 'Image'}
              fill
              loader={sanityLoader}
              sizes="(max-width: 768px) 100vw, 800px"
              className="object-cover"
            />
          </div>
          {value.caption && (
            <div className="mt-2 text-sm text-gray-600 italic text-center">
              {value.caption}
            </div>
          )}
        </div>
      );
    },
    textAndImage: ({ value }) => {
      if (!value?.text || !value?.image?.asset?._ref) {
        return null;
      }
      
      const { layout = 'textLeft', imageWidth = 'medium' } = value;
      
      // Define classes for different image widths
      const imageWidthClasses = {
        small: layout === 'textLeft' ? 'md:w-3/10' : 'md:w-3/10',
        medium: layout === 'textLeft' ? 'md:w-1/2' : 'md:w-1/2',
        large: layout === 'textLeft' ? 'md:w-7/10' : 'md:w-7/10',
      };
      
      const textWidthClass = 
        imageWidth === 'small' ? 'md:w-7/10' : 
        imageWidth === 'large' ? 'md:w-3/10' : 
        'md:w-1/2';
      
      // Create a simple text renderer to avoid recursion
      const renderText = (text) => {
        // If text is not an array, return empty
        if (!Array.isArray(text)) return null;
        
        return (
          <div>
            {text.map((block, i) => {
              // Handle different block types
              if (block._type === 'block') {
                return (
                  <div key={i} className="mb-4">
                    {block.children.map((child, j) => (
                      <span key={j}>{child.text}</span>
                    ))}
                  </div>
                );
              }
              return null;
            })}
          </div>
        );
      };
      
      return (
        <div className="my-8">
          <div className={`flex flex-col ${layout === 'textLeft' ? 'md:flex-row' : 'md:flex-row-reverse'} gap-4 md:gap-8`}>
            <div className={`w-full ${textWidthClass}`}>
              <div className="prose prose-lg max-w-none">
                {renderText(value.text)}
              </div>
            </div>
            
            <div className={`w-full ${imageWidthClasses[imageWidth]}`}>
              <div className="relative aspect-square">
                <Image
                  src={urlForImage(value.image)}
                  alt={value.image.alt || ''}
                  fill
                  loader={sanityLoader}
                  sizes="(max-width: 768px) 100vw, 50vw"
                  className="rounded-lg object-cover"
                />
              </div>
              {value.image.caption && (
                <figcaption className="mt-2 text-sm text-center text-gray-500">
                  {value.image.caption}
                </figcaption>
              )}
            </div>
          </div>
        </div>
      );
    },
    video: ({ value }) => <VideoEmbed value={value} />,
    imageGrid: ({ value }) => {
      if (!value?.images || !value.images.length) {
        return null;
      }
      
      const { layout = 'equal' } = value;
      
      // Define width classes based on layout
      const getWidthClasses = (index) => {
        if (layout === 'equal') return 'w-1/2';
        if (layout === 'leftLarger' && index === 0) return 'w-2/3';
        if (layout === 'leftLarger' && index === 1) return 'w-1/3';
        if (layout === 'rightLarger' && index === 0) return 'w-1/3';
        if (layout === 'rightLarger' && index === 1) return 'w-2/3';
        return 'w-1/2';
      };
      
      return (
        <div className="my-8">
          <div className="flex flex-wrap -mx-2">
            {value.images.map((image, index) => (
              <div key={index} className={`px-2 ${getWidthClasses(index)}`}>
                <div className="relative aspect-square">
                  <Image
                    src={urlForImage(image)}
                    alt={image.alt || ''}
                    fill
                    loader={sanityLoader}
                    sizes="(max-width: 640px) 50vw, 33vw"
                    className="rounded-lg object-cover"
                  />
                </div>
                {image.caption && (
                  <figcaption className="mt-1 text-sm text-center text-gray-500">
                    {image.caption}
                  </figcaption>
                )}
              </div>
            ))}
          </div>
        </div>
      );
    }
    
  },
  marks: {
    link: ({ children, value }) => {
      const rel = !value.href.startsWith('/') ? 'noreferrer noopener' : undefined;
      return (
        <a 
          href={value.href} 
          rel={rel} 
          className="text-accent-color hover:underline break-words"
          target={!value.href.startsWith('/') ? '_blank' : undefined}
        >
          {children}
        </a>
      );
    },
    strong: ({ children }) => <strong className="font-bold">{children}</strong>,
    em: ({ children }) => <em className="italic">{children}</em>,
    underline: ({ children }) => <span className="underline">{children}</span>,
    code: ({ children }) => <code className="bg-gray-100 rounded px-1 py-0.5 font-mono text-sm break-words">{children}</code>,
    fontStyle: ({ children, value }) => {
      const fontClass = value?.fontFamily || 'default';
      return <span className={fontClass !== 'default' ? fontClass : ''}>{children}</span>;
    },
  },
  block: {
    h1: ({ children, value }) => {
      const fontStyle = value?.markDefs?.find(mark => mark._type === 'fontStyle')?.fontFamily || '';
      return <h1 className={`text-4xl md:text-5xl font-bold my-8 ${fontStyle} break-words`}>{children}</h1>;
    },
    h2: ({ children, value }) => {
      const fontStyle = value?.markDefs?.find(mark => mark._type === 'fontStyle')?.fontFamily || '';
      return <h2 className={`text-3xl md:text-4xl font-bold my-6 ${fontStyle} break-words`}>{children}</h2>;
    },
    h3: ({ children, value }) => {
      const fontStyle = value?.markDefs?.find(mark => mark._type === 'fontStyle')?.fontFamily || '';
      return <h3 className={`text-xl md:text-2xl font-bold my-4 ${fontStyle} break-words`}>{children}</h3>;
    },
    h4: ({ children, value }) => {
      const fontStyle = value?.markDefs?.find(mark => mark._type === 'fontStyle')?.fontFamily || '';
      return <h4 className={`text-lg md:text-xl font-bold my-3 ${fontStyle} break-words`}>{children}</h4>;
    },
    normal: ({ children }) => <p className="text-base mb-4 break-words">{children}</p>,
    blockquote: ({ children }) => (
      <blockquote className="border-l-4 border-accent-color pl-4 my-6 italic overflow-hidden">{children}</blockquote>
    ),
    alignLeft: ({ children }) => <p className="text-left text-base mb-4 break-words">{children}</p>,
    alignCenter: ({ children }) => <p className="text-center text-base mb-4 break-words">{children}</p>,
    alignRight: ({ children }) => <p className="text-right text-base mb-4 break-words">{children}</p>,
    alignJustify: ({ children }) => <p className="text-justify text-base mb-4 break-words">{children}</p>,
  },
  list: {
    bullet: ({ children }) => <ul className="list-disc pl-6 mb-6 break-words">{children}</ul>,
    number: ({ children }) => <ol className="list-decimal pl-6 mb-6 break-words">{children}</ol>,
  },
  listItem: {
    bullet: ({ children }) => <li className="mb-1 break-words">{children}</li>,
    number: ({ children }) => <li className="mb-1 break-words">{children}</li>,
  },
};

export default function PortableText({ value }) {
  return <SanityPortableText value={value} components={components} />;
} 